import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import { Box, Text } from 'grommet';
import {
  Seo, AppButton, UnderConstruction, ProductRowStats, SubmissionLoader,
} from '@Components/Control';
import { AuthNavWrapper } from '@Components/Navigation';
import { WidgetRenderer } from '@Components/Partial/Product/Dashboard';
import { withProductAuth } from '@Components/Layout';
import { useAppTheme } from '@Components/Context';
import {
  handleQuery,
  handleSort,
  handleFilter,
  initializeReviewedRows,
  updateRowReviewStatus,
  updateMultipleRowsReviewStatus,
} from '@Helpers/controlFunctions';
import { getSelectedMonetizationResultIds } from '@Helpers/resultsMonetization';
import {
  initiateResultsInfringementsRequest,
  clearResultsInfringementsRequest,
  initiateResultsInfringementsMetaRequest,
  clearResultsInfringementsMetaRequest,
  updateProductReviewSelectionsRequest,
  bulkUpdateReviewStatusByDomainRequest,
  initiateResultsMonetizationRequest,
  initiateLiveTrackerAnalyticsRequest,
  clearLiveTrackerAnalyticsRequest,
} from '@Actions';

const AllInfringementsPage = ({
  small,
  location,
  size,
  loading,
  submitLoading,
  fetchAllInfringements,
  clearAllInfringements,
  fetchAllInfringementsMeta,
  clearAllInfringementsMeta,
  infringementsData = null,
  infringementsMeta = null,
  updateSearchResultsReviewSelections,
  bulkUpdateReviewStatusByDomain,
  resultsDisabled = false,
  paymentPending = false,
  featureConfig,
  contentIdEnabled = false,
  monitoringEnabled = false,
  createResultsMonetizationRequest,
  hostingData = null,
  fetchHostingData,
  clearHostingData,
}) => {
  const {
    theme: { page, button },
    constructionImage,
    isDarkMode,
    disableResultsFeatures,
  } = useAppTheme();
  const [reviewedData, setReviewedData] = React.useState([]);
  const [initialLaterCount, setInitialLaterCount] = React.useState(null);
  const [selectedRowIds, setSelectedRowIds] = React.useState([]);
  const [query, setQuery] = React.useState({
    paging: {
      to: 1,
      size: 20,
    },
  });
  const watermarkGroupingKey = featureConfig?.liveTrackerWatermarkGroupingKey ?? null;

  /* eslint-disable camelcase */
  React.useEffect(() => {
    fetchHostingData();
    return () => clearHostingData();
  }, []);

  React.useEffect(() => {
    fetchAllInfringementsMeta({ page_uid: 'all_results' });
    fetchAllInfringements({ ...query });

    return () => {
      clearAllInfringementsMeta();
      clearAllInfringements();
    };
  }, [query]);

  const sendReviewedResults = () => {
    const filteredSelections = _.filter(
      reviewedData,
      ({ review_status, prev_status }) => review_status !== 'pending' && review_status !== prev_status,
    ).map(({ id, review_status, prev_status }) => ({
      id,
      review_status,
      prev_status,
    }));

    updateSearchResultsReviewSelections({
      data: filteredSelections,
      search_result_can_be_updated: true,
    }, () => {
      fetchAllInfringements({ ...query });
    });
    clearAllInfringements();
    setReviewedData([]);
    window.scrollTo(0, 0);
  };

  const handleBulkUpdateReviewStatusByDomain = (bulkQuery) => {
    bulkUpdateReviewStatusByDomain(bulkQuery, () => {
      clearAllInfringements();
      setReviewedData([]);
      fetchAllInfringements({ ...query });
      window.scrollTo(0, 0);
    });
  };

  const handleExportCSV = () => {
    fetchAllInfringements({ ...query }, true);
  };

  const laterCount = reviewedData && _.filter(reviewedData, (({ review_status }) => review_status === 'later')).length;
  let submitEnabled = reviewedData.some(({ review_status }) => review_status !== 'pending' && review_status !== 'later');

  if (laterCount !== initialLaterCount) {
    submitEnabled = reviewedData.some(({ review_status }) => review_status !== 'pending');
  }
  /* eslint-enable camelcase */

  const padding = small ? '1.5rem' : '2rem';
  const reviewable = infringementsData?.data && (infringementsData.data.find((dt) => dt.feature === 'review/update') !== undefined);
  const reviewWidget = infringementsData?.data?.find((dt) => dt.type === 'review-table');
  const rawReviewRows = reviewWidget?.value?.rows || [];
  const monetizationSelection = getSelectedMonetizationResultIds({
    headers: reviewWidget?.value?.header,
    rows: rawReviewRows.map((row) => (Array.isArray(row) ? row : (row.cells || row))),
    selectedRowIds,
  });
  const totalResults = infringementsData?.totalCount ?? 0;
  const resultsPerPageOptions = [10, 15, 20, 25, 50];
  const underMaintenance = (disableResultsFeatures || resultsDisabled);
  const usePageBg = isDarkMode ? page.background : page.altBg;
  const canRequestMonetization = Boolean(contentIdEnabled && monitoringEnabled);
  const monetizationDisabled = !monetizationSelection.allSelectedEligible;
  const monetizationTitle = !monetizationSelection.hasSelection
    ? 'Select one or more YouTube results'
    : 'Select only YouTube results';

  const requestMonetization = () => {
    if (!monetizationSelection.allSelectedEligible) return;

    createResultsMonetizationRequest(
      {
        // eslint-disable-next-line camelcase
        result_ids: monetizationSelection.eligibleIds,
      },
      () => {
        clearAllInfringements();
        setReviewedData([]);
        setSelectedRowIds([]);
        fetchAllInfringements({ ...query });
        window.scrollTo(0, 0);
      },
    );
  };

  const renderPageContent = () => {
    if (underMaintenance) {
      return (
        <Box flex align="center" justify="center" background={usePageBg}>
          <UnderConstruction
            imageUrl={constructionImage}
            header="Under Maintenance"
            message="System currently undergoing maintenance. Please check back later."
          />
        </Box>
      );
    }

    return (
      <Box flex background={usePageBg} pad={{ top: '0', bottom: padding }}>
        <Box width="100%" direction="row" justify="end" pad={{ vertical: '0.65rem', horizontal: '1.5rem' }}>
          <ProductRowStats
            isLast
            key="totalResults"
            small={small}
            label="Total Results:"
            stat={totalResults}
            color={page.primaryText}
          />
        </Box>
        <WidgetRenderer
          small={small}
          size={size}
          loading={loading}
          handleQuery={(parentKey, updateKey, value) => (
            handleQuery(query, setQuery, parentKey, updateKey, value, {
              paging: {
                to: 1,
                size: 20,
              },
            })
          )}
          handleSort={(sortValue, sortDirection) => (
            handleSort(query, setQuery, sortValue, sortDirection)
          )}
          handleFilter={(key, value, predicate, seperateValues, reset) => (
            handleFilter(query, setQuery, key, value, predicate, seperateValues, reset)
          )}
          filterSortMeta={infringementsMeta}
          searchQuery={query}
          widgetData={infringementsData?.data}
          padding={padding}
          selectedStatuses={reviewedData}
          initializeReviewedRows={(data) => (
            initializeReviewedRows(setReviewedData, data, setInitialLaterCount)
          )}
          updateRowReviewStatus={(editableId, selectedOption) => (
            updateRowReviewStatus(reviewedData, setReviewedData, editableId, selectedOption)
          )}
          updateMultipleRowsReviewStatus={(updateableRows, selectedOption) => (
            updateMultipleRowsReviewStatus(
              reviewedData, setReviewedData, updateableRows, selectedOption,
            )
          )}
          bulkUpdateReviewStatusByDomain={handleBulkUpdateReviewStatusByDomain}
          handleExportCSV={handleExportCSV}
          resultsPerPageOptions={resultsPerPageOptions}
          resultsPerPage={query.paging?.size}
          currentPage={query.paging?.to}
          totalResults={totalResults}
          paymentPending={paymentPending}
          watermarkGroupingKey={watermarkGroupingKey}
          onSelectionChange={setSelectedRowIds}
          hostingData={hostingData?.data}
          includeISPData
        />
        {infringementsData?.data && reviewable && (
          <Box
            width="100%"
            direction={small ? 'column' : 'row'}
            gap={small ? '1rem' : '2rem'}
            justify="between"
            align="center"
            pad={{ horizontal: padding, vertical: '2rem' }}
          >
            <Box width={small ? '100%' : '65%'}>
              <Text size="1rem" weight={500} color={page.primaryText}>
                We will automatically send a DMCA notice for results marked as
                infringements after submission.
              </Text>
            </Box>
            <Box pad={{ right: small ? '0rem' : '4rem' }}>
              <Box direction={small ? 'column' : 'row'} gap="0.75rem">
                {canRequestMonetization && (
                  <AppButton
                    overrideHover
                    width="12rem"
                    disabled={monetizationDisabled}
                    onClick={requestMonetization}
                    level="dynamicLarge"
                    color={button.primaryText}
                    bgColor={button.secondaryBg || button.primaryBg}
                    fontWeight={600}
                    label="Request Monetization"
                    title={monetizationDisabled ? monetizationTitle : null}
                  />
                )}
                <AppButton
                  overrideHover
                  width="10rem"
                  disabled={!submitEnabled || (reviewedData.length === 0)}
                  onClick={() => sendReviewedResults(reviewedData)}
                  level="dynamicLarge"
                  color={button.primaryText}
                  bgColor={button.primaryBg}
                  fontWeight={600}
                  label="Submit Review"
                  title={submitEnabled ? null : 'Please select a status option'}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'All infringements',
        subTitle: 'Results',
      }}
    >
      <Seo />
      {submitLoading && (
        <SubmissionLoader
          textColor={page.modalText}
          loaderColor={page.highlight}
          message="Fetching CSV export..."
          contentWidth="20rem"
        />
      )}
      {renderPageContent()}
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    infringementsData: state.productResults.infringements,
    infringementsMeta: state.productResults.infringementsMeta,
    loading: state.fetchLoader.dataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
    hostingData: state.analytics.liveTrackerData,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchAllInfringements: initiateResultsInfringementsRequest,
    clearAllInfringements: clearResultsInfringementsRequest,
    fetchAllInfringementsMeta: initiateResultsInfringementsMetaRequest,
    clearAllInfringementsMeta: clearResultsInfringementsMetaRequest,
    updateSearchResultsReviewSelections: updateProductReviewSelectionsRequest,
    bulkUpdateReviewStatusByDomain: bulkUpdateReviewStatusByDomainRequest,
    createResultsMonetizationRequest: initiateResultsMonetizationRequest,
    fetchHostingData: initiateLiveTrackerAnalyticsRequest,
    clearHostingData: clearLiveTrackerAnalyticsRequest,
  }, dispatch);
}

AllInfringementsPage.propTypes = {
  small: PropTypes.bool.isRequired,
  size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'mediumlarge', 'large']).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  fetchAllInfringements: PropTypes.func.isRequired,
  clearAllInfringements: PropTypes.func.isRequired,
  fetchAllInfringementsMeta: PropTypes.func.isRequired,
  clearAllInfringementsMeta: PropTypes.func.isRequired,
  updateSearchResultsReviewSelections: PropTypes.func.isRequired,
  bulkUpdateReviewStatusByDomain: PropTypes.func.isRequired,
  createResultsMonetizationRequest: PropTypes.func.isRequired,
  infringementsData: PropTypes.shape({
    totalCount: PropTypes.number.isRequired,
    query: PropTypes.shape({
      paging: PropTypes.shape({
        to: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]).isRequired,
        size: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]).isRequired,
      }),
    }).isRequired,
    data: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
      type: PropTypes.string.isRequired,
      statusOptions: PropTypes.arrayOf(PropTypes.shape({
        api: PropTypes.string.isRequired,
        display: PropTypes.string.isRequired,
      })).isRequired,
      unit: PropTypes.string,
      feature: PropTypes.string,
    }).isRequired).isRequired,
  }),
  infringementsMeta: PropTypes.shape({
    sort: PropTypes.shape({
      name: PropTypes.shape({
        options: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired).isRequired,
      }).isRequired,
      direction: PropTypes.shape({
        options: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired).isRequired,
      }).isRequired,
    }).isRequired,
    filters: PropTypes.shape({
      options: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        formatted: PropTypes.string.isRequired,
        validPredicates: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
      })).isRequired,
    }).isRequired,
  }),
  resultsDisabled: PropTypes.bool,
  paymentPending: PropTypes.bool.isRequired,
  featureConfig: PropTypes.shape({
    liveTrackerEstimatedSubscriptionFee: PropTypes.number,
    watermarkGroupingKey: PropTypes.string,
    showPrePostEventAnalysis: PropTypes.bool,
    liveTrackerWatermarkGroupingKey: PropTypes.string,
  }).isRequired,
  contentIdEnabled: PropTypes.bool,
  monitoringEnabled: PropTypes.bool,
  hostingData: PropTypes.shape({ data: PropTypes.array }),
  fetchHostingData: PropTypes.func.isRequired,
  clearHostingData: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(AllInfringementsPage));
